import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = ({ 
  onEmergency,
  onSyncData,
  onNavigateToLocation,
  onCallSupport,
  onViewReports,
  isOffline = false,
  lastSyncTime = null 
}) => {
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await onSyncData();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleEmergency = () => {
    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }
    
    const confirmed = window.confirm(
      `Deseja acionar o protocolo de emergência?\n\nIsso irá:\n• Notificar a central imediatamente\n• Enviar sua localização atual\n• Registrar o incidente no sistema`
    );
    
    if (confirmed) {
      onEmergency();
    }
  };

  const formatLastSync = () => {
    if (!lastSyncTime) return 'Nunca';
    
    const now = new Date();
    const sync = new Date(lastSyncTime);
    const diffMinutes = Math.floor((now - sync) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Agora mesmo';
    if (diffMinutes < 60) return `${diffMinutes} min atrás`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h atrás`;
    
    return sync?.toLocaleDateString('pt-BR');
  };

  const quickActionItems = [
    {
      id: 'emergency',
      label: 'Emergência',
      icon: 'AlertTriangle',
      variant: 'destructive',
      action: handleEmergency,
      description: 'Acionar protocolo SOS'
    },
    {
      id: 'navigation',
      label: 'Navegação',
      icon: 'Navigation',
      variant: 'default',
      action: onNavigateToLocation,
      description: 'Abrir GPS/Maps'
    },
    {
      id: 'support',
      label: 'Suporte',
      icon: 'Headphones',
      variant: 'outline',
      action: onCallSupport,
      description: 'Ligar para central'
    },
    {
      id: 'reports',
      label: 'Relatórios',
      icon: 'FileText',
      variant: 'ghost',
      action: onViewReports,
      description: 'Ver relatórios'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-card-foreground">
          Ações Rápidas
        </h3>
        
        {/* Connection Status */}
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isOffline ? 'bg-error' : 'bg-success'}`}></div>
          <span className="text-xs text-muted-foreground">
            {isOffline ? 'Offline' : 'Online'}
          </span>
        </div>
      </div>
      {/* Sync Status */}
      <div className="bg-muted rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon 
              name={isSyncing ? "RefreshCw" : "Wifi"} 
              size={16} 
              className={`${isSyncing ? 'animate-spin' : ''} ${isOffline ? 'text-error' : 'text-success'}`}
            />
            <div>
              <p className="text-sm font-medium text-card-foreground">
                {isOffline ? 'Modo Offline' : 'Sincronizado'}
              </p>
              <p className="text-xs text-muted-foreground">
                Última sync: {formatLastSync()}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSync}
            disabled={isSyncing}
            iconName={isSyncing ? "RefreshCw" : "RefreshCcw"}
            className={isSyncing ? "animate-spin" : ""}
          >
            {isSyncing ? 'Sincronizando...' : 'Sync'}
          </Button>
        </div>
      </div>
      {/* Quick Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        {quickActionItems?.map((item) => (
          <Button
            key={item?.id}
            variant={item?.variant}
            onClick={item?.action}
            className="touch-target flex-col h-auto py-4 px-3"
          >
            <Icon name={item?.icon} size={24} className="mb-2" />
            <span className="text-sm font-medium">{item?.label}</span>
            <span className="text-xs opacity-75 mt-1">{item?.description}</span>
          </Button>
        ))}
      </div>
      {/* Offline Notice */}
      {isOffline && (
        <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="WifiOff" size={16} className="text-warning mt-0.5" />
            <div>
              <p className="text-sm font-medium text-warning">
                Modo Offline Ativo
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Suas ações serão sincronizadas quando a conexão for restaurada.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Emergency Instructions */}
      <div className="mt-4 p-3 bg-error/5 border border-error/10 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Shield" size={16} className="text-error mt-0.5" />
          <div>
            <p className="text-sm font-medium text-error">
              Protocolo de Emergência
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Em caso de emergência, use o botão SOS. Sua localização será enviada automaticamente para a central.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;